import { Building2, Dumbbell, ShieldCheck, Star, UserRound } from 'lucide-react';
import type { ComponentType } from 'react';

import type { LoginContextOption } from '../widget-api/auth-contexts';
import { EmbedMessageBlock } from './embed-message-block';
import { LoaderRing } from './block-loader';

type ContextSelectScreenProps = {
  title: string;
  subtitle: string;
  contexts: LoginContextOption[];
  busyId: string | null;
  error: string | null;
  onSelect: (context: LoginContextOption) => void;
  /** `studio` — карточки студий (клиент), `role` — карточки ролей (тренер). */
  variant: 'studio' | 'role';
};

type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

function roleIcon(role: string): IconComponent {
  switch (role.trim().toLowerCase()) {
    case 'trainer':
      return Dumbbell;
    case 'superadmin':
    case 'platform_staff':
      return Star;
    case 'org_main_admin':
    case 'admin':
    case 'branch_admin':
    case 'staff':
      return ShieldCheck;
    default:
      return UserRound;
  }
}

function optionTitle(context: LoginContextOption, variant: 'studio' | 'role'): string {
  if (variant === 'studio') {
    return context.org_name?.trim() || context.label;
  }
  return context.label;
}

function optionSubtitle(context: LoginContextOption, variant: 'studio' | 'role'): string | null {
  if (variant === 'studio') {
    return context.label && context.label !== optionTitle(context, variant) ? context.label : null;
  }
  return context.org_name?.trim() || null;
}

export function ContextSelectScreen({
  title,
  subtitle,
  contexts,
  busyId,
  error,
  onSelect,
  variant
}: ContextSelectScreenProps) {
  return (
    <div className="space-y-5 w-full min-w-0" data-testid="context-select-screen">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text)]">{title}</h1>
        <p className="text-sm leading-relaxed text-[var(--text-muted)]">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {contexts.map((context) => {
          const Icon = variant === 'studio' ? Building2 : roleIcon(context.role);
          const busy = busyId === context.id;
          const secondary = optionSubtitle(context, variant);
          return (
            <button
              key={context.id}
              type="button"
              data-testid={`context-option-${context.id}`}
              disabled={busyId !== null}
              onClick={() => onSelect(context)}
              className="group relative flex w-full items-start gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 text-left shadow-[var(--shadow-card)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--accent)] hover:shadow-md active:translate-y-0 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] disabled:pointer-events-none disabled:opacity-60"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--bg-soft)] text-[var(--accent)] transition-colors group-hover:bg-[var(--accent-bg)] group-hover:text-[var(--accent-fg)]">
                {busy ? (
                  <LoaderRing size="sm" hole="soft" className="!h-5 !w-5" />
                ) : (
                  <Icon className="h-6 w-6" aria-hidden />
                )}
              </div>
              <div className="min-w-0 flex-1 pr-2">
                <span className="block truncate text-base font-bold leading-snug text-[var(--text)] transition-colors group-hover:text-[var(--accent)]">
                  {optionTitle(context, variant)}
                </span>
                {secondary ? (
                  <span className="mt-0.5 block text-[13px] text-[var(--text-muted)]">{secondary}</span>
                ) : null}
                {busy ? <span className="mt-0.5 block text-[13px] text-[var(--text-muted)]">Входим…</span> : null}
              </div>
              <div
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] transition-all duration-200 group-hover:translate-x-1 group-hover:text-[var(--accent)]"
                aria-hidden="true"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>

      {error ? (
        <EmbedMessageBlock variant="error" size="field" className="w-full" role="alert" aria-live="assertive">
          {error}
        </EmbedMessageBlock>
      ) : null}
    </div>
  );
}
